import { supabase } from '../../../../shared/supabase/supabaseClient';
import {
  selectStockIdsForMovementHistory,
  type StockScopeRow,
} from '../../domain/stockHistoryScope';
import type {
  GetStockMovementHistoryOptions,
  StockMovementHistoryFilters,
  StockMovementRow,
  StockRow,
} from '../../domain/stockTypes';

/**
 * ADR-017: el historial de movimientos es 100% remoto (`inventory.stock_movements`
 * en Supabase). No hay path local: SQLite operativo fue eliminado y un fallback
 * vacío escondía errores de red. Los errores remotos propagan al caller para que
 * la UI muestre estado de error + retry en vez de una lista vacía silenciosa.
 *
 * El historial completo es owner-only (#868): sin `useRemoteTeamHistory` estas
 * funciones devuelven `[]` y la UI muestra el estado "solo el dueño" explícito.
 */

type MovementDbRow = {
  id: string;
  delta: number;
  reason: string | null;
  unit_cost: number | null;
  created_at: string;
  performed_by_user_id: string | null;
  origin_device_id: string | null;
  reference_id: string | null;
};

function mapMovementRow(row: MovementDbRow): StockMovementRow {
  return {
    id: row.id,
    delta: row.delta,
    reason: row.reason,
    unitCost: row.unit_cost ?? null,
    createdAt: row.created_at,
    performedByUserId: row.performed_by_user_id ?? undefined,
    originDeviceId: row.origin_device_id ?? undefined,
    referenceId: row.reference_id ?? undefined,
  };
}

async function fetchRemoteStockMovementHistory(params: {
  productId: string;
  branchId: string;
  variantId?: string | null;
  limit: number;
  offset: number;
  filters?: StockMovementHistoryFilters;
}): Promise<StockMovementRow[]> {
  // Resolver el alcance: para una celda de variante solo su fila de stock;
  // para el producto (fila parent/agrupada) todas sus filas en la sucursal,
  // así el historial incluye los movimientos de todas las variantes.
  // Antes se resolvía con product_id + branch_id + .maybeSingle() sin
  // variant_id → productos con más de una fila de stock rompían la query.
  const { data: stockRows, error: stockErr } = await supabase
    .schema('inventory')
    .from('stocks')
    .select('id, variant_id')
    .eq('product_id', params.productId)
    .eq('branch_id', params.branchId);

  if (stockErr) {
    throw stockErr;
  }
  const stockIds = selectStockIdsForMovementHistory(
    (stockRows ?? []) as StockScopeRow[],
    params.variantId
  );
  if (stockIds.length === 0) {
    return [];
  }

  let q = supabase
    .schema('inventory')
    .from('stock_movements')
    .select('id, delta, reason, unit_cost, created_at, performed_by_user_id, origin_device_id, reference_id')
    .in('stock_id', stockIds)
    .order('created_at', { ascending: false });

  const f = params.filters;
  if (f?.fromCreatedAt) {
    q = q.gte('created_at', f.fromCreatedAt);
  }
  if (f?.toCreatedAt) {
    q = q.lte('created_at', f.toCreatedAt);
  }
  if (f?.reasons && f.reasons.length > 0) {
    const orParts = f.reasons.map((r) => {
      if (r === 'sale') return 'reason.like.sale:%';
      if (r === 'sale_void') return 'reason.like.sale_void:%';
      return `reason.eq.${r}`;
    });
    q = q.or(orParts.join(','));
  } else if (f?.reason && f.reason !== 'all') {
    if (f.reason === 'sale') {
      q = q.like('reason', 'sale:%');
    } else if (f.reason === 'sale_void') {
      q = q.like('reason', 'sale_void:%');
    } else {
      q = q.eq('reason', f.reason);
    }
  }

  const from = params.offset;
  const to = params.offset + params.limit - 1;
  const { data, error } = await q.range(from, to);
  if (error) {
    throw error;
  }
  return (data ?? []).map((row) => mapMovementRow(row as MovementDbRow));
}

/**
 * Historial de movimientos para una celda de stock en sucursal.
 * Con `useRemoteTeamHistory` (owner) consulta Supabase. Sin ese flag devuelve
 * `[]` — el historial del equipo es owner-only y la UI lo comunica explícito.
 * Los errores remotos propagan (nunca se degradan a lista vacía).
 */
export const getStockMovementHistory = async (
  stock: StockRow,
  limit = 20,
  offset = 0,
  filters?: StockMovementHistoryFilters,
  options?: GetStockMovementHistoryOptions
): Promise<StockMovementRow[]> => {
  if (!options?.useRemoteTeamHistory) {
    return [];
  }

  return fetchRemoteStockMovementHistory({
    productId: stock.productId,
    branchId: stock.branchId,
    variantId: stock.variantId ?? null,
    limit,
    offset,
    filters,
  });
};

/**
 * Exportación / CSV: hasta `maxRows` movimientos que cumplen filtros (orden reciente primero).
 * Misma política owner-only que `getStockMovementHistory`; los errores remotos propagan.
 */
export const getStockMovementHistoryForExport = async (
  stock: StockRow,
  filters?: StockMovementHistoryFilters,
  maxRows = 10_000,
  options?: GetStockMovementHistoryOptions
): Promise<StockMovementRow[]> => {
  if (!options?.useRemoteTeamHistory) {
    return [];
  }

  return fetchRemoteStockMovementHistory({
    productId: stock.productId,
    branchId: stock.branchId,
    variantId: stock.variantId ?? null,
    limit: maxRows,
    offset: 0,
    filters,
  });
};
